import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Typography,
  Box,
  CircularProgress,
  Divider
} from '@mui/material';
import { useFlow } from '../../../contexts/FlowContext';

const ImportFlowsDialog = ({ open, onClose }) => {
  const { listImportableFlows, importFlow, isLoading } = useFlow();

  const [importableFlows, setImportableFlows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [importingFlowId, setImportingFlowId] = useState(null);

  useEffect(() => {
    if (open) {
      loadImportableFlows();
    }
  }, [open]);

  const loadImportableFlows = async () => {
    setLoading(true);
    setError(null);
    try {
      const flows = await listImportableFlows();
      setImportableFlows(flows);
    } catch (err) {
      setError(err.message || 'Failed to load flows from WhatsApp');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setImportableFlows([]);
    setError(null);
    setImportingFlowId(null);
    onClose();
  };

  const handleImport = async (whatsappFlowId) => {
    setImportingFlowId(whatsappFlowId);
    setError(null);
    try {
      await importFlow(whatsappFlowId);
      // Reload the list to update status
      await loadImportableFlows();
    } catch (err) {
      setError(err.message || 'Failed to import flow');
    } finally {
      setImportingFlowId(null);
    }
  };

  const getManagedStatusChip = (flow) => {
    switch (flow.managedStatus) {
      case 'MANAGED':
        return <Chip label="Managed" color="success" size="small" />;
      case 'OTHER_ENVIRONMENT':
        return (
          <Chip
            label={flow.managingEnvironmentName || 'Other environment'}
            color="warning"
            size="small"
          />
        );
      case 'UNMANAGED':
      default:
        return <Chip label="Unmanaged" color="default" size="small" variant="outlined" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'PUBLISHED':
        return 'success.main';
      case 'DRAFT':
        return 'warning.main';
      case 'DEPRECATED':
        return 'error.main';
      default:
        return 'text.secondary';
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Import Existing Flows</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : importableFlows.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No flows found in WhatsApp Business Account
          </Typography>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Shows all flows in your WhatsApp Business Account. Import unmanaged flows to start managing them in this environment.
            </Typography>
            <List>
              {importableFlows.map((flow, index) => (
                <React.Fragment key={flow.whatsappFlowId}>
                  {index > 0 && <Divider />}
                  <ListItem sx={{ py: 2 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1">{flow.name}</Typography>
                          {getManagedStatusChip(flow)}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                          <Typography variant="body2" component="span">
                            Status:{' '}
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{ color: getStatusColor(flow.status) }}
                            >
                              {flow.status}
                            </Typography>
                          </Typography>
                          <Typography variant="body2" color="text.secondary" component="span">
                            ID: {flow.whatsappFlowId}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      {flow.canImport && (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleImport(flow.whatsappFlowId)}
                          disabled={isLoading || importingFlowId === flow.whatsappFlowId}
                        >
                          {importingFlowId === flow.whatsappFlowId ? 'Importing...' : 'Import'}
                        </Button>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportFlowsDialog;
